import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Edit, Trash2, FileText, Target, CheckCircle, Clock, AlertCircle, Star, Download, AlertTriangle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import auditQuestionService from '../../../../../../services/audit_services/auditQuestionService';
import exportQuestionService from '../../../../../../services/audit_services/export_pdf_services/exportQuestionService';
import attachmentService from '../../../../../../services/audit_services/attachmentService';

export default function AuditQuestionDetails() {
  const { auditPlanId, sectionId, questionId } = useParams();
  const { hasAnyRole, authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [auditQuestion, setAuditQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRoleInPlan, setUserRoleInPlan] = useState(null); // User's role in the audit plan
  const [auditPlanStatus, setAuditPlanStatus] = useState(null); // Audit plan status

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    isLoading: false
  });

  const [downloadingAttachments, setDownloadingAttachments] = useState({});

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    setDownloadingAttachments(prev => ({ ...prev, [attachmentId]: true }));
    try {
      const result = await attachmentService.downloadAttachment(authTokens.access, attachmentId);
      if (!result.success) {
        showError(result.error || 'Erreur lors du téléchargement');
      }
    } catch (error) {
      showError('Erreur lors du téléchargement du fichier');
    } finally {
      setDownloadingAttachments(prev => ({ ...prev, [attachmentId]: false }));
    }
  };

  // Can edit if:
  // 1. User has AuditUser or AuditManager role
  // 2. User is an auditor in the audit plan
  // 3. Audit plan status is 'in_progress'
  const canEdit = hasAnyRole(['AuditUser','AuditManager']) && 
                  userRoleInPlan === 'auditor' && 
                  auditPlanStatus === 'in_progress';

  useEffect(() => {
    fetchAuditQuestionDetails();
  }, [auditPlanId, sectionId, questionId]);

  const fetchAuditQuestionDetails = async () => {
    setLoading(true);
    
    try {
      const result = await auditQuestionService.getAuditQuestionDetail(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionId
      );
      
      if (result.success) {
        setAuditQuestion(result.data);
        
        // Set user role and audit plan status from response
        if (result.data.my_role_in_audit_plan) {
          setUserRoleInPlan(result.data.my_role_in_audit_plan);
        }
        if (result.data.audit_plan) {
          setAuditPlanStatus(result.data.audit_plan?.status);
        }
      } else {
        showError(result.error || 'Error loading audit question details');
      }
    } catch (error) {
      console.error('Error fetching audit question details:', error);
      showError('Error loading audit question details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    
    try {
      const result = await auditQuestionService.deleteAuditQuestion(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionId
      );
      
      if (result.success) {
        showSuccess(`Question deleted successfully`);
        navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`);
      } else {
        showError(result.error || 'Error deleting audit question');
        setDeleteModal(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Error deleting audit question:', error);
      showError('Error deleting audit question');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      showSuccess('Generating PDF...');
      const result = await exportQuestionService.exportQuestionPDF(authTokens.access, questionId);
      
      if (result.success) {
        showSuccess('PDF downloaded successfully');
      } else {
        showError(result.error || 'Error exporting PDF');
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Error exporting PDF. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending', icon: Clock },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed', icon: CheckCircle },
      in_review: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Review', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const getQuestionTypeLabel = (type) => {
    const types = {
      'yes_no': 'Yes/No',
      'text': 'Text',
      'multiple_choice': 'Multiple Choice',
      'rating': 'Rating',
      'file_upload': 'File Upload'
    };
    return types[type] || type;
  };

  const getQuestionTypeBadge = (type) => {
    const typeConfig = {
      'yes_no': { bg: 'bg-blue-100', text: 'text-blue-800' },
      'text': { bg: 'bg-green-100', text: 'text-green-800' },
      'multiple_choice': { bg: 'bg-purple-100', text: 'text-purple-800' },
      'rating': { bg: 'bg-orange-100', text: 'text-orange-800' },
      'file_upload': { bg: 'bg-pink-100', text: 'text-pink-800' }
    };

    const config = typeConfig[type] || typeConfig.text;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {getQuestionTypeLabel(type)}
      </span>
    );
  };

  const renderAnswerDisplay = () => {
    if (!auditQuestion.answer) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600 text-center">Aucune réponse fournie pour le moment</p>
        </div>
      );
    }

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start justify-between mb-3">
          <h4 className="text-sm font-semibold text-green-900">Réponse</h4>
          {auditQuestion.answered_at && (
            <span className="text-xs text-green-600">
              Répondu le {new Date(auditQuestion.answered_at).toLocaleDateString()}
            </span>
          )}
        </div>
        
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-green-800 mb-1">Réponse:</p>
            <p className="text-sm text-green-700">{auditQuestion.answer}</p>
          </div>

          {auditQuestion.notes && (
            <div>
              <p className="text-sm font-medium text-green-800 mb-1">Notes:</p>
              <p className="text-sm text-green-700">{auditQuestion.notes}</p>
            </div>
          )}

          {auditQuestion.attachments && auditQuestion.attachments.length > 0 && (
            <div>
              <p className="text-sm font-medium text-green-800 mb-2">Pièces jointes:</p>
              <div className="space-y-2">
                {auditQuestion.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-100 rounded p-2">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-800">{attachment.file_name}</span>
                      <span className="text-xs text-green-600">({attachment.file_size || 'Taille inconnue'})</span>
                    </div>
                    <button 
                      onClick={() => window.open(attachment.file_url, '_blank')}
                      className="text-green-600 hover:text-green-800 text-xs flex items-center"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Voir
                    </button>
                    <button 
                      onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                      disabled={downloadingAttachments[attachment.id]}
                      className="text-blue-600 hover:text-blue-800 text-xs flex items-center disabled:opacity-50 ml-2"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      {downloadingAttachments[attachment.id] ? '...' : 'Télécharger'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {auditQuestion.answered_by && (
            <div className="pt-2 border-t border-green-200">
              <p className="text-xs text-green-600">
                Répondu par <span className="font-medium">{auditQuestion.answered_by}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderQuestionOptions = () => {
    if (auditQuestion.question_type === 'multiple_choice' && auditQuestion.options) {
      return (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Available Options:</p>
          <div className="flex flex-wrap gap-2">
            {auditQuestion.options.map((option, index) => (
              <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                {option}
              </span>
            ))}
          </div>
        </div>
      );
    }

    if (auditQuestion.question_type === 'rating' && auditQuestion.scale) {
      return (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Rating Scale:</p>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">1</span>
            <div className="flex space-x-1">
              {[...Array(auditQuestion.scale)].map((_, index) => (
                <Star key={index} className="h-4 w-4 text-gray-300" />
              ))}
            </div>
            <span className="text-sm text-gray-600">{auditQuestion.scale}</span>
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de la question...</p>
        </div>
      </div>
    );
  }

      if (!auditQuestion) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Question non trouvée</p>
        <Link to={`/dashboard/audit/questions${sectionId ? `?audit_section=${sectionId}` : ''}`} className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux questions
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center space-x-2 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-600 text-sm font-semibold rounded-full">
                {auditQuestion.order || '#'}
              </span>
              <h1 className="text-2xl font-bold text-gray-900">Détails de la question</h1>
            </div>
            <p className="text-sm text-gray-600">
              Informations sur la question d'audit
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(auditQuestion.status)}
          {getQuestionTypeBadge(auditQuestion.question_type)}
          
          {/* Export PDF Button */}
          <button
            onClick={handleExportPDF}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter en PDF
          </button>
          
          {canEdit && (
            <div className="flex items-center space-x-2">
              <Link
                to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/edit`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Link>
 
            </div>
          )}
        </div>
      </div>

      {/* Status Message - Show when user is auditor but can't edit due to status */}
      {hasAnyRole(['AuditUser','AuditManager']) && userRoleInPlan === 'auditor' && auditPlanStatus !== 'in_progress' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900">Modification restreinte</p>
              <p className="text-sm text-yellow-800 mt-1">
                Les questions et les actions de recommandation ne peuvent être modifiées que lorsque le statut du plan d'audit est "En cours". 
                Statut actuel: <span className="font-semibold">{auditPlanStatus?.replace('_', ' ').toUpperCase()}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section and Audit Plan Links */}
      {auditQuestion.audit_section && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Target className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm text-green-900">
                Section:{' '}
                <Link
                  to={`/dashboard/audit/sections/${auditQuestion.audit_section.id}?audit_plan=${auditQuestion.audit_section.audit_plan?.id}`}
                  className="font-semibold hover:underline"
                >
                  {auditQuestion.audit_section.section_name}
                </Link>
              </span>
            </div>
            {auditQuestion.audit_section.audit_plan && (
              <>
                <span className="text-green-300">•</span>
                <span className="text-sm text-green-900">
                  Plan d'audit:{' '}
                  <Link
                    to={`/dashboard/audit/my-plans/${auditQuestion.audit_section.audit_plan.id}`}
                    className="font-semibold hover:underline"
                  >
                    {auditQuestion.audit_section.audit_plan.title}
                  </Link>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Question Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations sur la question</h3>
        
        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Nom de la question</p>
            <p className="text-base text-gray-900">{auditQuestion.question_name}</p>
          </div>

          {auditQuestion.question_description && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Description</p>
              <p className="text-sm text-gray-600">{auditQuestion.question_description}</p>
            </div>
          )}

          {auditQuestion.location && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Localisation</p>
              <p className="text-sm text-gray-900">{auditQuestion.location}</p>
            </div>
          )}

          {auditQuestion.requirements && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Exigences</p>
              <p className="text-sm text-gray-600">{auditQuestion.requirements}</p>
            </div>
          )}

          {auditQuestion.interview_plan && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Plan d'entretien</p>
              <p className="text-sm text-gray-600">{auditQuestion.interview_plan}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Statut</p>
              <p className="text-sm text-gray-900">{auditQuestion.status}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Résultat de l'entretien</p>
              <p className="text-sm text-gray-900">{auditQuestion.interview_result || 'Non défini'}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Confirmation de l'audité</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                auditQuestion.auditee_response_confirmation === 'confirmed' ? 'bg-green-100 text-green-800' :
                auditQuestion.auditee_response_confirmation === 'rejected' ? 'bg-red-100 text-red-800' :
                auditQuestion.auditee_response_confirmation === 'needs_clarification' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {auditQuestion.auditee_response_confirmation || 'En attente'}
              </span>
            </div>
          </div>

          {/* Question Type Specific Options */}
          {renderQuestionOptions()}
        </div>
      </div>

      {/* Answer Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Réponse et résultats</h3>
        
        <div className="space-y-6">
          {/* Answer */}
          {renderAnswerDisplay()}

          {/* Results */}
          {auditQuestion.results && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Résultats</p>
              <p className="text-sm text-gray-600">{auditQuestion.results}</p>
            </div>
          )}

        </div>
      </div>

      {/* Risk Assessment Section */}
      {auditQuestion.question_risks && auditQuestion.question_risks.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Évaluation des risques</h3>
          
          <div className="space-y-4">
            {auditQuestion.question_risks.map((questionRisk) => (
              <div key={questionRisk.id} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-orange-900 mb-2">{questionRisk.risk_name}</h4>
                    {questionRisk.description && (
                      <p className="text-sm text-orange-700 mb-3">{questionRisk.description}</p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm font-medium text-orange-800 mb-1">Probabilité</p>
                        <p className="text-sm text-orange-700 capitalize">
                          {questionRisk.probability_display || questionRisk.probability}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-orange-800 mb-1">Conséquence</p>
                        <p className="text-sm text-orange-700">{questionRisk.consequence}</p>
                      </div>
                    </div>

                    {questionRisk.actions && questionRisk.actions.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-orange-800 mb-2">Actions associées</p>
                        <div className="space-y-2">
                          {questionRisk.actions.map((action) => (
                            <div key={action.id} className="bg-white border border-orange-200 rounded p-2">
                              <div className="flex items-start justify-between">
                                <p className="text-sm text-gray-900 flex-1">{action.action}</p>
                                <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                                  action.is_completed 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {action.is_completed ? 'Terminé' : 'En cours'}
                                </span>
                              </div>
                              {action.due_date && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Échéance: {new Date(action.due_date).toLocaleDateString()}
                                </p>
                              )}
                              {action.user_assigned_name && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Assigné à: {action.user_assigned_name}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachments Section */}
      {auditQuestion.attachments && auditQuestion.attachments.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pièces jointes</h3>
          <div className="space-y-3">
            {auditQuestion.attachments.map((attachment, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                    <p className="text-xs text-gray-500">
                      {attachment.file_size && `${attachment.file_size} • `}
                      Téléchargé le {new Date(attachment.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => window.open(attachment.file_url, '_blank')}
                    className="inline-flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Voir
                  </button>
                  <button 
                    onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                    disabled={downloadingAttachments[attachment.id]}
                    className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-50"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {downloadingAttachments[attachment.id] ? 'Téléchargement...' : 'Télécharger'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Initial Observations */}
      {auditQuestion.initial_observations && auditQuestion.initial_observations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Observations initiales</h3>
          <div className="space-y-3">
            {auditQuestion.initial_observations.map((observation, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-900">{observation.observation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPIs */}
      {auditQuestion.kpis && auditQuestion.kpis.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">KPIs</h3>
          <div className="space-y-3">
            {auditQuestion.kpis.map((kpi, index) => (
              <div key={index} className="bg-green-50 rounded-lg p-3">
                <p className="text-sm font-medium text-green-900">{kpi.kpi_name}</p>
                <p className="text-sm text-green-700">{kpi.kpi_value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Observations */}
      {(auditQuestion.observations && auditQuestion.observations.length > 0) || auditQuestion.observation_effectiveness !== null ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Observations</h3>
          
          {/* Observation Effectiveness */}
          {auditQuestion.observation_effectiveness !== null && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-green-900">Efficacité de l'observation</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  auditQuestion.observation_effectiveness ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {auditQuestion.observation_effectiveness ? 'Oui' : 'Non'}
                </span>
              </div>
            </div>
          )}

          {auditQuestion.observations && auditQuestion.observations.length > 0 && (
            <div className="space-y-3">
              {auditQuestion.observations.map((observation, index) => (
                <div key={index} className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-sm text-yellow-900">{observation.observation}</p>
                </div>
              ))}
            </div>
          )}

          {(!auditQuestion.observations || auditQuestion.observations.length === 0) && auditQuestion.observation_effectiveness !== null && (
            <p className="text-sm text-gray-500 text-center">Aucune observation enregistrée pour le moment</p>
          )}
        </div>
      ) : null}

      {/* Recommendations */}
      {auditQuestion.recommendations && auditQuestion.recommendations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recommandations</h3>
            <Link
              to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations`}
              className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <Target className="h-4 w-4 mr-2" />
              Voir les actions de recommandation
            </Link>
          </div>
          <div className="space-y-3">
            {auditQuestion.recommendations.map((recommendation, index) => (
              <div key={index} className="bg-green-50 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <p className="text-sm text-green-900 flex-1">{recommendation.recommendation}</p>
                  <div className="flex items-center space-x-2 ml-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      recommendation.priority === 'high' ? 'bg-red-100 text-red-800' :
                      recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {recommendation.priority}
                    </span>
                    {canEdit && auditQuestion.observation_effectiveness === false && (
                      <Link
                        to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations/create?recommendation=${index}`}
                        className="inline-flex items-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                      >
                        Créer une action
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Métadonnées</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Créé par</p>
              <p className="text-sm text-gray-900">{auditQuestion.created_by_name || 'Inconnu'}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Date de création</p>
              <p className="text-sm text-gray-900">{new Date(auditQuestion.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Dernière mise à jour</p>
              <p className="text-sm text-gray-900">{new Date(auditQuestion.updated_at).toLocaleDateString()}</p>
            </div>

            {auditQuestion.answered_by && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Répondu par</p>
                <p className="text-sm text-gray-900">{auditQuestion.answered_by}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, isLoading: false })}
        onConfirm={handleDelete}
        title="Supprimer la question d'audit"
        message={`Êtes-vous sûr de vouloir supprimer cette question ? Cela supprimera également toutes les réponses et fichiers de preuve associés. Cette action ne peut pas être annulée.`}
        confirmText="Supprimer la question"
        cancelText="Annuler"
        isLoading={deleteModal.isLoading}
        type="danger"
      />
    </div>
  );
}